import { OpusDecoder, OpusDecoderSampleRate } from 'opus-decoder';

interface PlayerOptions {
  sampleRate?: number;
  channels?: number;
}

const SUPPORTED_SAMPLE_RATES: OpusDecoderSampleRate[] = [8000, 12000, 16000, 24000, 48000];

const resolveSampleRate = (value?: number): OpusDecoderSampleRate => {
  if (value && SUPPORTED_SAMPLE_RATES.includes(value as OpusDecoderSampleRate)) {
    return value as OpusDecoderSampleRate;
  }
  return 16000;
};

export class OpusPlayer {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private decoder: OpusDecoder<OpusDecoderSampleRate> | null = null;
  private nextPlaybackTime = 0;
  private muted = false;
  private volume = 1;
  private channels = 1;
  private sampleRate: OpusDecoderSampleRate = 16000;

  async initialize(options?: PlayerOptions): Promise<void> {
    const sampleRate = resolveSampleRate(options?.sampleRate ?? this.sampleRate);
    const channels = options?.channels ?? this.channels;

    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate });
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);
    } else if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    this.sampleRate = sampleRate;
    this.channels = channels;
    this.nextPlaybackTime = this.audioContext?.currentTime ?? 0;

    if (this.decoder) {
      await this.decoder.reset();
    } else {
      this.decoder = new OpusDecoder({ sampleRate, channels });
      await this.decoder.ready;
    }

    this.applyGain();
  }

  private applyGain(): void {
    if (this.gainNode) {
      this.gainNode.gain.value = this.muted ? 0 : this.volume;
    }
  }

  async resume(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  async suspend(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend();
    }
  }

  async playOpusFrame(frame: Uint8Array): Promise<void> {
    if (!this.decoder) {
      await this.initialize();
    }
    const audioContext = this.audioContext;
    const gainNode = this.gainNode;
    const decoder = this.decoder;

    if (!audioContext || !gainNode || !decoder) {
      return;
    }

    // Validate frame is not empty
    if (!frame || frame.length === 0) {
      return;
    }

    try {
      const decoded = decoder.decodeFrame(frame);
      
      // Check for decode errors
      if (decoded.errors && decoded.errors.length > 0) {
        // Only log first few errors to avoid console spam
        const errorCount = decoded.errors.length;
        if (errorCount <= 3) {
          console.warn('Opus decode errors:', decoded.errors);
        }
        return;
      }

      if (decoded.samplesDecoded === 0) {
        return;
      }

      const buffer = audioContext.createBuffer(
        decoded.channelData.length,
        decoded.samplesDecoded,
        decoded.sampleRate,
      );

      decoded.channelData.forEach((channelData, index) => {
        const channelCopy = new Float32Array(channelData.length);
        channelCopy.set(channelData);
        buffer.copyToChannel(channelCopy, index, 0);
      });

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNode);

      const startTime = Math.max(this.nextPlaybackTime, audioContext.currentTime);
      source.start(startTime);
      this.nextPlaybackTime = startTime + decoded.samplesDecoded / decoded.sampleRate;
    } catch (error) {
      // Silently ignore decode errors - they're often due to incomplete frames or network issues
      // Only log if it's not a known Opus error
      if (error instanceof Error && !error.message.includes('OPUS_INVALID_PACKET')) {
        console.warn('Opus decode error:', error);
      }
    }
  }

  setVolume(value: number): void {
    this.volume = Math.min(Math.max(value, 0), 1);
    this.applyGain();
  }

  setMuted(value: boolean): void {
    this.muted = value;
    this.applyGain();
  }

  getVolume(): number {
    return this.volume;
  }

  isMuted(): boolean {
    return this.muted;
  }

  async reset(): Promise<void> {
    await this.decoder?.reset();
    this.nextPlaybackTime = this.audioContext?.currentTime ?? 0;
  }

  async dispose(): Promise<void> {
    await this.suspend();
    this.decoder?.free();
    this.decoder = null;
    this.gainNode?.disconnect();
    this.gainNode = null;
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
  }
}

